import React, { useCallback } from 'react';
import { PolygonNameInput } from './PolygonNameInput';
import { PolygonInfoBanner } from './PolygonInfoBanner';
import { VertexRow } from './VertexRow';
import styles from './GeoFeaturePanel.module.scss';

interface Vertex {
  lat: string;
  lng: string;
}

interface Props {
  name: string;
  onNameChange: (v: string) => void;
  vertices: Vertex[];
  onVerticesChange: (vertices: Vertex[]) => void;
  isDrawing: boolean;
}

/**
 * Highly modular, memoized parent component for GeoFeature polygon configuration.
 * Decomposes state into isolated subcomponents to eliminate unnecessary re-renders.
 */
const PolygonBody: React.FC<Props> = React.memo(({
  name,
  onNameChange,
  vertices,
  onVerticesChange,
  isDrawing,
}) => {
  const addVertex = useCallback(() => {
    onVerticesChange([...vertices, { lat: '', lng: '' }]);
  }, [onVerticesChange, vertices]);

  const removeVertex = useCallback((i: number) => {
    onVerticesChange(vertices.filter((_, idx) => idx !== i));
  }, [onVerticesChange, vertices]);

  const updateVertexField = useCallback((i: number, field: 'lat' | 'lng', val: string) => {
    onVerticesChange(vertices.map((vx, idx) => (idx === i ? { ...vx, [field]: val } : vx)));
  }, [onVerticesChange, vertices]);

  const hasVertices = vertices.some((v) => v.lat !== '' || v.lng !== '');
  const validVertexCount = vertices.filter((v) => v.lat && v.lng).length;

  return (
    <>
      <PolygonNameInput name={name} onNameChange={onNameChange} />

      <PolygonInfoBanner
        isDrawing={isDrawing}
        hasVertices={hasVertices}
        validVertexCount={validVertexCount}
      />

      <div className={styles.orDivider}>or</div>

      <div className={styles.section}>Vertices</div>

      {vertices.map((vx, i) => (
        <VertexRow
          key={i}
          index={i}
          lat={vx.lat}
          lng={vx.lng}
          onUpdate={updateVertexField}
          onRemove={removeVertex}
        />
      ))}

      <button className={styles.addVertexBtn} type="button" onClick={addVertex}>
        + Add vertex
      </button>
    </>
  );
});

PolygonBody.displayName = 'PolygonBody';

export default PolygonBody;
